"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  RadarController,
  PointElement,
  RadialLinearScale,
  CategoryScale,
  Filler,
  Legend,
  Title,
  Tooltip,
  LineElement,
} from "chart.js";
import SnowFlakeModal from "../companies/SnowFlakeModal";
import ChartSkeleton from "@/src/components/companies/ChartSkeleton";

// Register the components used by the radar chart
ChartJS.register(
  RadarController,
  PointElement,
  RadialLinearScale,
  CategoryScale,
  Filler,
  Legend,
  Title,
  Tooltip,
  LineElement // Ensure LineElement is registered here
);

const SnowFlakeChart = ({ ticker }) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chartFetched, setChartFetched] = useState(false);

  // Define fetchData using useCallback
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    if (!ticker) return;

    try {
      const response = await axios.post(
        `https://api.researchfrc.com/api/companies/snowflake`,
        { ticker: ticker },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.data.success) {
        throw new Error("Failed to fetch data");
      }

      setChartFetched(true);
      const { past_value, growth, health, profit, value } = response.data.data;
      const scores = [past_value, growth, health, profit, value].map(
        (letter) => 5 - (letter.charCodeAt(0) - "A".charCodeAt(0))
      );

      setChartData({
        labels: ["Past Performance (Stock Price)", "Revenue Growth", "Health", "Profitability", "Value"],
        datasets: [
          {
            label: `${ticker} Stock Analysis`,
            data: scores,
            fill: true,
            backgroundColor: "rgba(18, 159, 135, 0.3)",
            borderColor: "rgb(18, 159, 135)",
            pointBackgroundColor: "rgb(255, 99, 132)",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "rgb(255, 99, 132)",
          },
        ],
      });
    } catch (error) {
      console.error("There is no snowflake data for: ", ticker);
    } finally {
      setIsLoading(false);
    }
  }, [ticker]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const renderChart = () => {
      if (chartData && chartRef.current) {
        const newChartInstance = new ChartJS(chartRef.current, {
          type: "radar",
          data: chartData,
          options: {
            
            scales: {
              r: {
                angleLines: {
                  display: false,
                },
                suggestedMin: 0,
                suggestedMax: 5,
              },
            },
            elements: {
              line: {
                borderWidth: 2,
              },
            },
          },
        });
        return newChartInstance;
      }
    };

    const chartInstance = renderChart(); // Render and get the new chart instance

    return () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    };
  }, [chartData]); // Use chartData as the dependency to recreate the chart when data changes

  return (
    <div className="w-fullmax-w-sm mx-auto">
      {isLoading ? (
        <ChartSkeleton />
      ) : (
        <>
          {chartFetched &&  
            <div>
              <canvas ref={chartRef}></canvas>
              <div className="flex items-center justify-center">
                <SnowFlakeModal />
              </div>
            </div>
          }
        </>
      )}
    </div>
  );
};

export default SnowFlakeChart;
